import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { api } from '../../../core/network/api';
import { IconClose } from '../../../common/icons';
import { useModalDialog } from '../../../common/hooks/use-modal-dialog';
import { useGeneratePdf } from '../../reports/contexts/reports';
import { downloadPdf } from '../../reports/utils/shell';
import { pregnancyPaths } from '../queries/paths';
import { buildDxReport } from '../utils/report';

import './styles/report-modal.css';

// Modal de informe PDF de Dx (por OPU). Filtra qué sesiones incluir: una finca /
// todas las fincas de un cliente / todas — derivadas de las sesiones TE de la OPU.
// Al generar: por cada preñez del alcance se trae su ensamblado (estado + última dx
// + forecast con las fechas estimadas siguientes) y se arma el PDF.
export default function DxReportModal({ opuCode, sessions, onClose }) {
    const { mutateAsync: generatePdf } = useGeneratePdf();
    const cardRef = useModalDialog(onClose);
    const [scope, setScope] = useState({ type: 'all' });
    const [busy, setBusy] = useState(false);

    const list = useMemo(() => (sessions || []).filter((s) => s.status !== 0), [sessions]);

    const clients = useMemo(() => {
        const byClient = new Map();
        list.forEach((s) => {
            const cid = s.client?.id ?? 'none';
            if (!byClient.has(cid)) byClient.set(cid, { id: cid, name: s.client?.name || 'Sin cliente', farms: new Map() });
            const c = byClient.get(cid);
            if (s.farm && !c.farms.has(s.farm.id)) c.farms.set(s.farm.id, s.farm);
        });
        return [...byClient.values()].map((c) => ({ ...c, farms: [...c.farms.values()] }));
    }, [list]);

    const matches = (s) => {
        if (scope.type === 'all') return true;
        if (scope.type === 'client') return (s.client?.id ?? 'none') === scope.id;
        if (scope.type === 'farm') return s.farm?.id === scope.id;
        return false;
    };
    const selected = list.filter(matches);
    // ids de preñez del alcance (cada transfer tiene a lo sumo una preñez).
    const pregIds = [...new Set(selected.flatMap((s) => (s.transfers || []).map((t) => t.pregnancy?.id).filter(Boolean)))];
    // finca + preservación de la sesión por preñez (fuente fiable: el ensamblado de
    // preñez NO trae recipient_farm ni storage del embrión — el storage solo vive en
    // las sesiones TE de la OPU). pregnancyId → { farm, storage }.
    const metaByPreg = useMemo(() => {
        const m = new Map();
        list.forEach((s) => (s.transfers || []).forEach((t) => {
            if (t.pregnancy?.id != null) m.set(t.pregnancy.id, { farm: s.farm || null, storage: t.embryo?.storage || null });
        }));
        return m;
    }, [list]);

    const scopeLabel = () => {
        if (scope.type === 'all') return 'Todas las fincas';
        if (scope.type === 'client') return clients.find((c) => c.id === scope.id)?.name || 'Cliente';
        if (scope.type === 'farm') { for (const c of clients) { const f = c.farms.find((x) => x.id === scope.id); if (f) return f.name; } }
        return '';
    };

    const isSel = (s) => (s.type === scope.type) && (s.type === 'all' || s.id === scope.id);

    const generate = async () => {
        if (!pregIds.length) { toast.error('No hay preñeces para el filtro elegido'); return; }
        setBusy(true);
        const failures = [];
        try {
            const items = [];
            for (const pid of pregIds) {
                try {
                    const data = await api.get(pregnancyPaths.detail(pid)).then((r) => r.data);
                    // adjunta finca y preservación de la sesión (el ensamblado de preñez no las trae).
                    const meta = metaByPreg.get(pid) || {};
                    items.push({ ...data, _farm: meta.farm || data.transfer?.recipient_farm || null, _storage: meta.storage || null });
                } catch { failures.push(`#${pid}`); }
            }
            if (!items.length) { toast.error(`No se pudo generar: sin acceso a las preñeces (${failures.join(', ')})`); return; }
            const { html, filename, options } = buildDxReport(items, { opuCode, scopeLabel: scopeLabel() });
            const blob = await generatePdf({ html, filename, options });
            downloadPdf(blob, filename);
            if (failures.length) toast(`Informe generado (omitidas sin acceso: ${failures.length})`);
            else toast.success('Informe generado');
            onClose();
        } catch (err) {
            if (err?.status !== 403) toast.error(err?.message || 'No se pudo generar el informe');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="dx-report">
            <div className="dx-report__overlay" onClick={onClose} aria-hidden="true" />
            <div className="dx-report__card" role="dialog" aria-modal="true" aria-label="Generar informe de diagnósticos" ref={cardRef}>
                <header className="dx-report__head">
                    <h2 className="dx-report__title">Informe de Dx{opuCode ? ` · OPU ${opuCode}` : ''}</h2>
                    <button type="button" className="dx-report__close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
                </header>

                <div className="dx-report__body">
                    {!list.length ? (
                        <p className="dx-report__hint">Esta OPU no tiene sesiones de transferencia.</p>
                    ) : (
                        <>
                            <span className="dx-report__label">Incluir en el informe</span>
                            <div className="dx-report__scope">
                                <label className="dx-report__opt">
                                    <input type="radio" name="dx-report-scope" checked={isSel({ type: 'all' })} onChange={() => setScope({ type: 'all' })} disabled={busy} />
                                    <span>Todas las fincas</span>
                                </label>
                                {clients.map((c) => (
                                    <div key={c.id} className="dx-report__client">
                                        {c.farms.length > 1 && (
                                            <label className="dx-report__opt dx-report__opt--group">
                                                <input type="radio" name="dx-report-scope" checked={isSel({ type: 'client', id: c.id })} onChange={() => setScope({ type: 'client', id: c.id })} disabled={busy} />
                                                <span>Todas las fincas de {c.name}</span>
                                            </label>
                                        )}
                                        {c.farms.map((f) => (
                                            <label key={f.id} className="dx-report__opt dx-report__opt--farm">
                                                <input type="radio" name="dx-report-scope" checked={isSel({ type: 'farm', id: f.id })} onChange={() => setScope({ type: 'farm', id: f.id })} disabled={busy} />
                                                <span>{f.name}<small> · {c.name}</small></span>
                                            </label>
                                        ))}
                                    </div>
                                ))}
                            </div>
                            <span className="dx-report__hint">{pregIds.length} preñez(es) en el alcance</span>
                        </>
                    )}
                </div>

                <footer className="dx-report__foot">
                    <button type="button" className="dx-report__cancel" onClick={onClose} disabled={busy}>Cancelar</button>
                    <button type="button" className="dx-report__primary" onClick={generate} disabled={busy || !pregIds.length}>
                        {busy ? 'Generando…' : 'Generar PDF'}
                    </button>
                </footer>
            </div>
        </div>
    );
}
